import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NumberInput } from '../ui/number-input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Crosshair, Users, Settings, X, Search, ChevronRight, ChevronDown, AlertTriangle, Plus } from 'lucide-react';
import { deviceDisplayName } from '../../utils/deviceName';

export const SEND_TO_OPTIONS = [
  { value: 'this_device', label: 'This device only', description: 'Command sent only to the device that triggered the event', icon: Crosshair },
  { value: 'other_in_group', label: 'Other devices in rule', description: 'All other selected devices except the trigger device', icon: Users },
  { value: 'all_in_group', label: 'All devices in rule', description: 'Every device selected in this rule including the trigger device', icon: Users },
  { value: 'specific_devices', label: 'Specific devices', description: 'Choose specific target devices from any device in the system', icon: Settings },
];

// Command Action Configuration sub-component. Shared by Event Rules and the
// Fleet Service/Maintenance reminder stages (used for both trigger and clear).
export const CommandActionConfig = ({ config, onChange, devices, selectedDeviceIds, hasSmsNotif, testIdPrefix }) => {
  const sendTo = config.send_to || 'this_device';
  const isMultiTarget = sendTo !== 'this_device';
  const source = isMultiTarget ? 'custom' : (config.source || 'custom');
  const selectedDevices = devices.filter(d => selectedDeviceIds.includes(d.id));
  const deviceCommands = selectedDevices.flatMap(d => (d.custom_commands || []).map(c => ({ ...c, device_name: deviceDisplayName(d) || d.imei })));

  const [targetSearch, setTargetSearch] = useState('');
  const [targetCollapsed, setTargetCollapsed] = useState(new Set());
  const targetDeviceIds = config.target_device_ids || [];

  const targetDevices = devices.filter(d => targetDeviceIds.includes(d.id));
  const targetProtocols = [...new Set(targetDevices.map(d => d.protocol).filter(Boolean))];
  const hasTargetMixedProtocols = targetProtocols.length > 1;

  const filteredTargetDevices = useMemo(() => {
    const q = targetSearch.toLowerCase();
    return devices.filter(d => !q || (deviceDisplayName(d) || '').toLowerCase().includes(q))
      .sort((a, b) => (deviceDisplayName(a) || '').toLowerCase().localeCompare((deviceDisplayName(b) || '').toLowerCase()));
  }, [devices, targetSearch]);

  const targetDeviceGroups = useMemo(() => {
    const groups = {};
    filteredTargetDevices.forEach(d => { const g = d.device_group || 'Ungrouped'; if (!groups[g]) groups[g] = []; groups[g].push(d); });
    return Object.entries(groups).sort(([a], [b]) => { if (a === 'Ungrouped') return 1; if (b === 'Ungrouped') return -1; return a.localeCompare(b); });
  }, [filteredTargetDevices]);

  const toggleTargetDevice = (deviceId) => {
    const current = config.target_device_ids || [];
    const next = current.includes(deviceId) ? current.filter(id => id !== deviceId) : [...current, deviceId];
    onChange('target_device_ids', next);
  };

  const toggleTargetGroup = (groupDevices) => {
    const ids = groupDevices.map(d => d.id);
    const current = config.target_device_ids || [];
    const allSelected = ids.every(id => current.includes(id));
    if (allSelected) {
      onChange('target_device_ids', current.filter(id => !ids.includes(id)));
    } else {
      onChange('target_device_ids', [...new Set([...current, ...ids])]);
    }
  };

  return (
    <div className="space-y-3 pl-1">
      {/* Send To selector */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Send Command To</Label>
        <div className="grid grid-cols-2 gap-1.5">
          {SEND_TO_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isActive = sendTo === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`flex items-start gap-2 p-2 rounded-md border text-left transition-colors ${isActive ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:bg-muted/50'}`}
                onClick={() => {
                  onChange('send_to', opt.value);
                  if (opt.value !== 'this_device') {
                    if ((config.source || 'custom') === 'device_command') {
                      onChange('source', 'custom');
                    }
                    onChange('device_command_id', '');
                    onChange('template_id', '');
                  }
                }}
                data-testid={`${testIdPrefix}-send-to-${opt.value}`}
              >
                <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className={`text-xs font-medium ${isActive ? 'text-primary' : ''}`}>{opt.label}</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">{opt.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Specific devices picker */}
      {sendTo === 'specific_devices' && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Target Devices ({targetDeviceIds.length} selected)</Label>
            {targetDeviceIds.length > 0 && (
              <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground" onClick={() => onChange('target_device_ids', [])}>Clear all</button>
            )}
          </div>
          {(() => {
            const staleTargetIds = targetDeviceIds.filter(id => !devices.some(d => d.id === id));
            if (!staleTargetIds.length) return null;
            return (
              <div className="p-2 rounded border border-amber-500/40 bg-amber-500/10 space-y-1" data-testid={`${testIdPrefix}-stale-targets`}>
                <p className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
                  {staleTargetIds.length} selected device(s) are no longer in your account. Commands to them are blocked — remove them:
                </p>
                <div className="flex flex-wrap gap-1">
                  {staleTargetIds.map(id => (
                    <span key={id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 font-mono text-amber-800 dark:text-amber-300">
                      Removed device ({id.slice(0, 8)}…)
                      <button type="button" onClick={() => toggleTargetDevice(id)} title="Remove" data-testid={`${testIdPrefix}-remove-stale-${id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input value={targetSearch} onChange={(e) => setTargetSearch(e.target.value)} placeholder="Search devices..." className="pl-7 h-7 text-xs" data-testid={`${testIdPrefix}-target-search`} />
          </div>
          <ScrollArea className="h-[330px] border rounded">
            {targetDeviceGroups.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-3">No devices found</p>
            ) : targetDeviceGroups.map(([groupName, groupDevices]) => {
              const allInGroup = groupDevices.every(d => targetDeviceIds.includes(d.id));
              const isCollapsed = targetCollapsed.has(groupName);
              return (
                <div key={groupName}>
                  <div
                    className="flex items-center gap-1.5 px-2 py-1 bg-muted border-b cursor-pointer sticky top-0 z-10"
                    onClick={() => setTargetCollapsed(prev => { const n = new Set(prev); n.has(groupName) ? n.delete(groupName) : n.add(groupName); return n; })}
                  >
                    {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    <Checkbox checked={allInGroup} onCheckedChange={() => toggleTargetGroup(groupDevices)} onClick={(e) => e.stopPropagation()} />
                    <span className="text-xs font-medium flex-1">{groupName}</span>
                    <span className="text-[10px] text-muted-foreground">{groupDevices.length}</span>
                  </div>
                  {!isCollapsed && groupDevices.map(d => (
                    <div key={d.id} className="flex items-center gap-2 py-0.5 px-3 hover:bg-muted/50 cursor-pointer" onClick={() => toggleTargetDevice(d.id)}>
                      <Checkbox checked={targetDeviceIds.includes(d.id)} />
                      <span className="text-xs flex-1">{deviceDisplayName(d)}</span>
                      {d.protocol && <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 shrink-0">{d.protocol}</Badge>}
                    </div>
                  ))}
                </div>
              );
            })}
          </ScrollArea>
          {hasTargetMixedProtocols && (
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              Selected target devices have mixed protocols ({targetProtocols.join(', ')}). Commands are protocol-specific and may not work correctly.
            </p>
          )}
        </div>
      )}

      {/* Command Source */}
      <div className="space-y-1">
        <Label className="text-xs">Command Source</Label>
        {isMultiTarget ? (
          <div className="flex items-center gap-2">
            <div className="h-8 flex items-center px-3 text-xs border rounded-md bg-muted/50 flex-1">Custom Ad-hoc Command</div>
            <p className="text-[10px] text-muted-foreground shrink-0">Device commands unavailable for multi-target</p>
          </div>
        ) : (
          <Select value={source} onValueChange={(v) => onChange('source', v)}>
            <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-source`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="device_command">Device Custom Command</SelectItem>
              <SelectItem value="custom">Custom Ad-hoc Command</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      {source === 'device_command' && !isMultiTarget && (
        <div className="space-y-1">
          <Label className="text-xs">Select Device Command</Label>
          {(() => {
            const isSms = (config.delivery_method || 'gprs') === 'sms';
            const filteredCmds = isSms ? deviceCommands.filter(c => c.command_mode !== 'sequence') : deviceCommands;
            return filteredCmds.length > 0 ? (
              <Select value={config.device_command_id || ''} onValueChange={(v) => onChange('device_command_id', v)}>
                <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-device-cmd`}><SelectValue placeholder="Select command..." /></SelectTrigger>
                <SelectContent>
                  {filteredCmds.map((cmd) => (
                    <SelectItem key={cmd.id} value={cmd.id}>
                      {cmd.button_label || cmd.command_text} — {cmd.device_name} {cmd.command_mode === 'sequence' ? `(${cmd.command_sequence?.length || 0} steps)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">{isSms ? 'No single-command device commands found (sequence commands are not available for SMS delivery).' : 'No custom commands found on selected devices. Add commands in Device Management.'}</p>
            );
          })()}
        </div>
      )}
      {source === 'custom' && (
        <div className="space-y-2">
          {(config.delivery_method || 'gprs') !== 'sms' && (
            <div className="flex items-center gap-3">
              <Label className="text-xs">Command Mode:</Label>
              <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                <button type="button" className={`px-3 py-1 text-xs rounded-md transition-colors ${config.command_mode !== 'sequence' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => onChange('command_mode', 'single')} data-testid={`${testIdPrefix}-mode-single`}>Single</button>
                <button type="button" className={`px-3 py-1 text-xs rounded-md transition-colors ${config.command_mode === 'sequence' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => { onChange('command_mode', 'sequence'); if (!config.command_sequence?.length) onChange('command_sequence', [{ command_text: '', delay_ms: 0 }, { command_text: '', delay_ms: 0 }]); }} data-testid={`${testIdPrefix}-mode-sequence`}>Sequence</button>
              </div>
            </div>
          )}
          {(config.command_mode !== 'sequence' || (config.delivery_method || 'gprs') === 'sms') && (
            <div className="space-y-1">
              <Label className="text-xs">Command Text</Label>
              <Input value={config.command_text || ''} onChange={(e) => onChange('command_text', e.target.value)} placeholder="setdigout 1" className="h-8 text-xs font-mono" data-testid={`${testIdPrefix}-cmd-text`} />
            </div>
          )}
          {config.command_mode === 'sequence' && (config.delivery_method || 'gprs') !== 'sms' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Sequence Steps ({(config.command_sequence || []).length})</Label>
                <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => onChange('command_sequence', [...(config.command_sequence || []), { command_text: '', delay_ms: 0 }])} data-testid={`${testIdPrefix}-seq-add`}><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
              {(config.command_sequence || []).map((step, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground w-4 text-right">{idx + 1}.</span>
                  <Input value={step.command_text || ''} onChange={(e) => { const seq = [...(config.command_sequence || [])]; seq[idx] = { ...seq[idx], command_text: e.target.value }; onChange('command_sequence', seq); }} placeholder={`Step ${idx + 1}`} className="h-7 text-xs font-mono flex-1" />
                  <NumberInput min="0" value={step.delay_ms || ''} onValueChange={(n) => { const seq = [...(config.command_sequence || [])]; seq[idx] = { ...seq[idx], delay_ms: n }; onChange('command_sequence', seq); }} placeholder="0" className="h-7 text-xs w-28" />
                  <span className="text-[10px] text-muted-foreground">ms</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onChange('command_sequence', (config.command_sequence || []).filter((_, i) => i !== idx))} disabled={(config.command_sequence || []).length <= 2}><X className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">Delivery Method</Label>
        <Select value={config.delivery_method || 'gprs'} onValueChange={(v) => onChange('delivery_method', v)}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-delivery`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="gprs">GPRS (TCP)</SelectItem>
            {hasSmsNotif && <SelectItem value="sms">SMS</SelectItem>}
          </SelectContent>
        </Select>
        {!hasSmsNotif && <p className="text-[10px] text-muted-foreground">SMS delivery requires SMS privilege to be enabled.</p>}
      </div>
    </div>
  );
};

export default CommandActionConfig;
